/**
 * HostingModeToggle — pill in the desktop top-nav right cluster that
 * swaps between guest (Explore) and host (Hosting) context.
 *
 * Innovation over a flat tab bar: host is a MODE, not a peer tab. That's
 * how Airbnb, Turo, and most P2P marketplaces present the duality on
 * desktop. Only rendered when the user has host potential (owns a
 * vehicle or is in an organization).
 */
import { useReducedMotion } from 'react-native-reanimated'
import React, { useState } from 'react'
import { View, Text, Platform } from 'react-native'
import { useRouter, usePathname } from 'expo-router'
import { Home, Search } from '@/components/ui/icons'
import { Pressable } from '../ui/Pressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { COLORS, FONTS, SPACING } from '../../lib/brand'
import { RADIUS, useSemanticColors, DURATION } from '../../lib/theme/tokens'

const isWeb = Platform.OS === 'web'

export function HostingModeToggle() {
  const reducedMotion = useReducedMotion()
  const router = useRouter()
  const pathname = usePathname()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [hover, setHover] = useState(false)

  const inHostingMode = pathname?.startsWith('/hosting') ?? false

  const onPress = () => {
    if (inHostingMode) {
      router.navigate('/explore' as never)
    } else {
      router.navigate('/hosting' as never)
    }
  }

  const bg = inHostingMode ? COLORS.coral.DEFAULT : hover ? colors.surfaceRaised : colors.surface
  const fg = inHostingMode ? '#FFFFFF' : hover ? colors.textPrimary : colors.textSecondary

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      accessibilityRole="button"
      accessibilityLabel={inHostingMode ? 'Cambiar a modo huésped' : 'Cambiar a modo anfitrión'}
      testID="topnav.hosting-toggle"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.xs,
        paddingVertical: SPACING.xs + 2,
        paddingHorizontal: SPACING.md,
        borderRadius: RADIUS.full,
        backgroundColor: bg,
        borderWidth: 1,
        borderColor: inHostingMode
          ? 'transparent'
          : hover
            ? `${COLORS.coral.DEFAULT}33`
            : colors.border,
        ...(isWeb
          ? ({
              transition: reducedMotion
                ? undefined
                : `background-color ${DURATION.fast}ms ease-out, border-color ${DURATION.fast}ms ease-out, transform ${DURATION.fast}ms cubic-bezier(0.16, 1, 0.3, 1)`,
              transform: hover ? 'translateY(-1px)' : 'translateY(0)',
            } as unknown as object)
          : null),
      }}
    >
      {inHostingMode ? (
        <Home size={14} color={fg} strokeWidth={2.2} />
      ) : (
        <Search size={14} color={fg} strokeWidth={2.2} />
      )}
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.bodyBold,
          color: fg,
        }}
      >
        {inHostingMode ? 'Modo anfitrión' : 'Rentar mi vehículo'}
      </Text>
      {/* Unused reference, silences future dead-import linters */}
      <View style={{ width: 0, height: 0 }} />
    </Pressable>
  )
}
